import React from "react"

import Typography from "../../../components/shared/Typography"
import { ScrollReveal } from "../../WebsiteBuilderV2/components/shared/ScrollReveal"

const Footer = ({ isMobile = false }) => {
  const currentYear = new Date().getFullYear()

  return (
    <ScrollReveal
      as="footer"
      variant="fadeIn"
      threshold={0.08}
      className="bg-white pb-20 pt-0"
      style={{ borderTop: "none" }}>
      <div className={`container mx-auto ${isMobile ? "w-full px-6" : "max-w-splash-container px-5"}`}>
        {/* Top Divider */}
        <div className={`h-px w-full bg-gray-light ${isMobile ? "mb-6" : "mb-0"}`} />

        {/* Logo and Columns */}
        <div
          className={`flex items-start justify-between ${
            isMobile ? "mb-12 flex-col gap-10" : "py-14 md:flex-col md:gap-8"
          }`}>
          {/* Logo */}
          <div className="flex items-center">
            <a href="/">
              <img src="/images/healme_logo.svg" alt="Heal.me" className="size-6" />
            </a>
          </div>

          {/* Navigation Columns */}
          <div className={`flex ${isMobile ? "flex-col gap-10" : "gap-[104px] md:flex-col md:gap-10"}`}>
            {/* Get started */}
            <div className={`flex flex-col ${isMobile ? "gap-4" : "gap-4"}`}>
              <Typography variant="h5" className="text-[18px] font-bold leading-[22.5px] text-black">
                Get started
              </Typography>
              <div className={`flex flex-col ${isMobile ? "gap-2" : "gap-2"}`}>
                <a href="/practitioner-search" className="text-gray-dark transition-colors hover:text-teal">
                  <Typography variant="body" className={isMobile ? "mb-2" : ""}>
                    Find a practitioner
                  </Typography>
                </a>
                <a href="/wellness" className="text-gray-dark transition-colors hover:text-teal">
                  <Typography variant="body">Explore services</Typography>
                </a>
              </div>
            </div>

            {/* For practitioners */}
            <div className={`flex flex-col ${isMobile ? "gap-4" : "gap-4"}`}>
              <Typography variant="h5" className="text-[18px] font-bold leading-[22.5px] text-black">
                For practitioners
              </Typography>
              <div className={`flex flex-col ${isMobile ? "gap-2" : "gap-2"}`}>
                <a href="/pro" className="text-gray-dark transition-colors hover:text-teal">
                  <Typography variant="body" className={isMobile ? "mb-2" : ""}>
                    Join as a practitioner
                  </Typography>
                </a>
                <a href="/login" className="text-gray-dark transition-colors hover:text-teal">
                  <Typography variant="body" className={isMobile ? "mb-2" : ""}>
                    Login
                  </Typography>
                </a>
                <a href="/practitioners_az" className="text-gray-dark transition-colors hover:text-teal">
                  <Typography variant="body">Directory (A-Z)</Typography>
                </a>
              </div>
            </div>

            {/* Resources */}
            <div className={`flex flex-col ${isMobile ? "gap-4" : "gap-4"}`}>
              <Typography variant="h5" className="text-[18px] font-bold leading-[22.5px] text-black">
                Resources
              </Typography>
              <div className={`flex flex-col ${isMobile ? "gap-2" : "gap-2"}`}>
                <a href="/wellness" className="text-gray-dark transition-colors hover:text-teal">
                  <Typography variant="body" className={isMobile ? "mb-2" : ""}>
                    Wellness A-Z
                  </Typography>
                </a>
                <a href="/about" className="text-gray-dark transition-colors hover:text-teal">
                  <Typography variant="body" className={isMobile ? "mb-2" : ""}>
                    About
                  </Typography>
                </a>
                <a href="/contact" className="text-gray-dark transition-colors hover:text-teal">
                  <Typography variant="body" className={isMobile ? "mb-2" : ""}>
                    Contact
                  </Typography>
                </a>
                <a href="/reviews" className="text-gray-dark transition-colors hover:text-teal">
                  <Typography variant="body" className={isMobile ? "mb-2" : ""}>
                    Heal.me Reviews
                  </Typography>
                </a>
                <a
                  href="https://heal.me/websites-for-reiki-practitioners"
                  className="text-gray-dark transition-colors hover:text-teal">
                  <Typography variant="body">Websites for Reiki Practitioners</Typography>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div
          className={`flex items-start justify-between border-t border-gray-light ${
            isMobile ? "flex-col gap-6 pt-8" : "flex-wrap gap-6 pt-8 md:flex-col"
          }`}>
          {/* Disclaimer */}
          <div className={`flex flex-col gap-[18px] ${isMobile ? "w-full" : "max-w-[520px] md:max-w-full"}`}>
            <Typography variant="small" className="text-[14px] leading-[21px] text-gray-dark">
              © Heal.me {currentYear}
            </Typography>
            <Typography variant="small" className="text-[13px] leading-[19.5px] text-gray-dark">
              Use of this website, content, and products are for informational purposes only. Heal.me does not provide
              medical advice, diagnosis, or treatment.
            </Typography>
            <div
              className={`flex items-center ${
                isMobile ? "justify-start gap-4" : "gap-4"
              } text-[13px] leading-[19.5px]`}>
              <a href="/privacy" className="text-gray-dark transition-colors hover:text-teal">
                Privacy
              </a>
              <a href="/terms" className="text-gray-dark transition-colors hover:text-teal">
                Terms
              </a>
              <a href="/careers" className="text-gray-dark transition-colors hover:text-teal">
                Careers
              </a>
            </div>
          </div>

          {/* Social Icons + App Store */}
          <div className={`flex flex-col items-end gap-4 ${isMobile ? "items-start" : ""}`}>
            <div className="flex items-center gap-5">
              <a
                href="https://twitter.com/healdotme"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-dark transition-colors hover:text-teal"
                aria-label="Twitter">
                <svg className="size-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
              <a
                href="https://pinterest.com/healdotme"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-dark transition-colors hover:text-teal"
                aria-label="Pinterest">
                <svg className="size-6" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_2654_1226)">
                    <path d="M6.7509 23.5815C6.76197 23.6698 6.7989 23.7526 6.85672 23.8185C6.91453 23.8844 6.99045 23.9304 7.07425 23.9502H7.18204C7.25939 23.9486 7.33519 23.9274 7.40282 23.8884C7.47046 23.8495 7.52787 23.794 7.57006 23.7267C8.60675 22.396 9.40278 20.8818 9.91976 19.2575C10.0275 18.8887 10.3509 17.5591 10.588 16.6094C10.5971 16.5631 10.6179 16.5201 10.6482 16.4848C10.6784 16.4495 10.7172 16.4231 10.7605 16.4083C10.8004 16.3868 10.8448 16.3755 10.8898 16.3755C10.9348 16.3755 10.9792 16.3868 11.0192 16.4083C11.7912 17.0465 12.7479 17.3967 13.7353 17.4027C17.9497 17.4027 21 13.4139 21 7.91668C21 4.00606 17.8311 -0.0498047 12.5281 -0.0498047C6.61078 0.0395808 3 4.44182 3 8.70997C3 11.3357 4.07784 13.3468 5.82395 14.0954C5.92191 14.1425 6.02857 14.1668 6.13653 14.1668C6.24448 14.1668 6.35114 14.1425 6.4491 14.0954C6.54524 14.0432 6.62956 13.9704 6.69646 13.8817C6.76336 13.7931 6.81131 13.6908 6.83713 13.5815C6.83713 13.4251 7.05269 12.6653 7.09581 12.4642C7.15869 12.2913 7.16969 12.1028 7.12738 11.9233C7.08506 11.7439 6.9914 11.5819 6.85868 11.4586C6.3089 10.7739 6.02458 9.90167 6.06108 9.01165C6.05472 8.18614 6.20976 7.36784 6.51685 6.60615C6.82393 5.84445 7.2767 5.15515 7.84782 4.57981C8.41894 4.00448 9.09658 3.55506 9.83985 3.25865C10.5831 2.96225 11.3766 2.82501 12.1725 2.85522C15.3305 2.85522 17.2922 4.72115 17.2922 7.72673C17.2922 11.6373 15.6431 14.5759 13.4551 14.5759C13.1796 14.584 12.9059 14.528 12.6541 14.412C12.4023 14.296 12.1788 14.1229 12 13.9055C11.839 13.6919 11.728 13.4425 11.6756 13.1769C11.6233 12.9113 11.631 12.6367 11.6982 12.3748C11.8383 11.7714 12.0216 11.1346 12.2048 10.5312C12.5442 9.56736 12.7545 8.55984 12.8299 7.53679C12.8526 7.24874 12.8167 6.95897 12.7247 6.68617C12.6326 6.41338 12.4864 6.16364 12.2954 5.95306C12.1045 5.74248 11.873 5.57575 11.6161 5.46364C11.3591 5.35152 11.0823 5.29651 10.8036 5.30215C9.2515 5.30215 8.04431 6.89992 8.04431 9.00048C8.04139 9.68232 8.1545 10.3594 8.37844 11.0005C8.40998 11.106 8.40998 11.219 8.37844 11.3245L6.79401 18.3748C6.48347 20.0941 6.46887 21.8569 6.7509 23.5815Z" />
                  </g>
                  <defs>
                    <clipPath id="clip0_2654_1226">
                      <rect width="24" height="24" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </a>
              <a
                href="https://facebook.com/healdotme"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-dark transition-colors hover:text-teal"
                aria-label="Facebook">
                <svg className="size-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </a>
              <a
                href="https://instagram.com/healdotme"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-dark transition-colors hover:text-teal"
                aria-label="Instagram">
                <svg className="size-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </a>
            </div>
            <a
              href="https://apps.apple.com/us/app/heal-me-book-holistic-care/id1599744549"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
              aria-label="Download on the App Store">
              <img
                src="/images/apple_store_badge.png"
                alt="Download on the App Store"
                className="h-10 max-h-10 w-auto shrink-0"
              />
            </a>
          </div>
        </div>
      </div>
    </ScrollReveal>
  )
}

export default Footer
